/* eslint-disable prefer-destructuring */
import { message } from "antd";

export const ErrorStatusMessages = {
  400: "Oops! Something went wrong. Please try again later.",
  401: "You are not signed in. Please log in to continue.",
  403: "Access denied. You do not have permission to perform this action.",
  404: "We couldn’t find what you are looking for.",
  405: "This action is not allowed. Please check and try again.",
  408: "The request took too long. Please try again.",
  409: "This action is not allowed at the moment. Please check and try again later.",
  410: "This content is no longer available.",
  413: "The file size is too large. Please use a smaller file and try again.",
  415: "Unsupported file format. Please use a different file type.",
  429: "Too many requests. Please wait and try again later.",
  500: "Something went wrong on our end. Please try again later.",
  501: "This request is not supported. Please check and try again.",
  502: "There’s an issue with the server. Please try again later.",
  503: "We’re experiencing high traffic. Please try again soon.",
  504: "The server took too long to respond. Please try again in a moment.",
};

const DEFAULT_ERROR_MESSAGE = "Something went wrong. Please try again later.";
const DEFAULT_ERROR_MESSAGE_DURATION = 3;

const getErrorMessage = (messages = [], code, fallbackErrorMsg) => {
  let validMessage = messages.find(
    (msg) =>
      msg && (typeof msg === "string" || (Array.isArray(msg) && msg.length > 0))
  );

  if (Array.isArray(validMessage)) {
    validMessage = validMessage.join(", ");
  }

  return (
    validMessage ||
    ErrorStatusMessages[code] ||
    fallbackErrorMsg ||
    DEFAULT_ERROR_MESSAGE
  );
};

/**
 * This function is used to show error message to the user where custom error message should be passed as a fallback message for the user's better understanding if the API fails.
 * @param error - error object from the API response or the error message
 * @param params - duration or custom error message or both in an object (type: number | string | {
  duration?: number;
  errorMsg?: string;
})
 * @returns void
 * @example
 * showAppError('Something went wrong');
 * showAppError('Something went wrong', 3);
 * showAppError('Something went wrong', 'Custom error message'); 
 */

export const showAppError = (error, params) => {
  // https://developer.mozilla.org/en-US/docs/Web/API/DOMException#aborterror
  // Error code 20 is use for AbortError
  if (error?.code === 20) {
    return;
  }
  let errorMsg = DEFAULT_ERROR_MESSAGE;
  const fallbackErrorMsg =
    typeof params === "object" && params.errorMsg
      ? params.errorMsg
      : (typeof params === "string" && params) || "";

  if (error?.code >= 400 || error?.code !== 0) {
    errorMsg = getErrorMessage(
      [error?.result?.message, error?.result],
      error.code,
      fallbackErrorMsg
    );
  }

  console.log(
    "error:",
    error,
    "errorResp:",
    error?.response || error?.result || error
  );

  if (error?.status >= 400) {
    const errorResp = error.response;

    errorMsg = getErrorMessage(
      [
        errorResp?.data?.result?.message,
        errorResp?.data?.result,
        errorResp?.result?.message,
        errorResp?.result,
        errorResp?.data?.message,
      ],
      error.status,
      fallbackErrorMsg
    );
  }

  switch (errorMsg) {
    case "Unauthorized":
      errorMsg = ErrorStatusMessages[401];
      break;
    case "Too Many Requests":
    case "Too many requests":
    case "Too many requests, please try again later.":
      errorMsg = ErrorStatusMessages[429];
      break;
    case "Internal Server Error":
      errorMsg = ErrorStatusMessages[500];
      break;
    default:
      break;
  }

  const errorStatusCode = error?.status || error?.code;

  console.log("errorStatusCode:", errorStatusCode);

  if ([401, 403, 429].includes(errorStatusCode)) {
    errorMsg = ErrorStatusMessages[errorStatusCode];
  }

  const finalErrorMsg = typeof error === "string" ? error : errorMsg;

  console.log("errorMsg:", finalErrorMsg);

  message.error({
    content: finalErrorMsg,
    duration:
      typeof params === "object" && params.duration
        ? params.duration
        : (typeof params === "number" && params) ||
          DEFAULT_ERROR_MESSAGE_DURATION,
    key: "errorAlert",
    type: "error",
  });
};
